import React, { ReactNode, useContext } from 'react';
import { UseBoolean } from 'react-hanger';
import { TransitionGroup } from 'react-transition-group';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import cn from 'clsx';
import _ from 'lodash';
import styles from './page-nav-menu-item.module.scss';
import StyledMuiListItem from './styled-list-item';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import PageNavMenuItemIcon from '@components-clean/page-nav/page-nav-menu-item-icon';
import { usePageNavMenuItem } from '@components-clean/page-nav/page-nav-menu-item.utils';
import { PageNavItem } from '@components-clean/page-nav/page-nav-menu-items.data';
import PageNavTooltip from '@components-clean/page-nav/page-nav-tooltip';
import { useLocalState, useTranslation } from 'hooks';
import { UserContext } from '@contexts/user-context';
import Constants from '@utils/constants';
import { AllRoutes } from '@utils/router/all-routes';

interface ActiveItemPerLevelInfo {
    topLevel?: { id: string; params?: Record<string, string> };
    secondLevel?: { id: string; params?: Record<string, string> };
    thirdLevel?: { id: string; params?: Record<string, string> };
}

export interface PageNavMenuItemProps {
    activeItemsInfo: ActiveItemPerLevelInfo;
    faIcon?: IconDefinition | IconProp;
    icon?: ReactNode;
    id: string;
    isPageNavCollapsed?: boolean;
    itemKey?: string;
    // eslint-disable-next-line react/no-unused-prop-types
    params?: Record<string, string>;
    label: string;
    level?: number;
    readOnly?: boolean;
    displayMode?: 'default' | 'collapsed';
    onClick?: (itemId: string, level: number, hasSubItems: boolean, params?: Record<string, string>) => void;
    tooltip?: string | undefined;
    searchActive: boolean;
    searchTerm: string;
    expandOnSearch?: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    suffixIcon?: ReactNode;
    currentPageItemsInfo: ActiveItemPerLevelInfo;
    expandCurrentPageItems: UseBoolean;
}

interface PageNavSubMenuProps {
    collapsed: boolean;
    activeItemsInfo: ActiveItemPerLevelInfo;
    items: PageNavItem[];
    level: number;
    isPageNavCollapsed?: boolean;
    displayMode?: 'default' | 'collapsed';
    onClick?: (itemId: string, level: number, hasSubItems: boolean, params?: Record<string, string>) => void;
    readOnly?: boolean;
    searchActive: boolean;
    searchTerm: string;
    expandOnSearch?: boolean;
    currentPageItemsInfo: ActiveItemPerLevelInfo;
    expandCurrentPageItems: UseBoolean;
}

interface RelativeMenuItemPositionWrapperProps {
    top?: number;
    level?: number;
    children: React.ReactNode;
    style?: React.CSSProperties;
}

const RelativeMenuItemPositionWrapper: React.FC<RelativeMenuItemPositionWrapperProps> = ({
    top = 0,
    level,
    children,
    style,
}) => <div style={level === 0 ? { position: 'relative', top, ...style } : { ...style }}>{children}</div>;

const Highlighted = ({ text, highlight }: { text: string; highlight: string }) => {
    if (!highlight.trim()) {
        return <span>{text}</span>;
    }
    const regex = new RegExp(`(${_.escapeRegExp(highlight)})`, 'gi');
    const parts = text.split(regex);

    return (
        <span>
            {parts
                .filter((part) => part)
                .map((part, index) =>
                    // eslint-disable-next-line react/no-array-index-key
                    regex.test(part) ? <mark key={index}>{part}</mark> : <span key={index}>{part}</span>,
                )}
        </span>
    );
};

export const PageNavMenuItem: React.FC<PageNavMenuItemProps> = ({
    activeItemsInfo,
    faIcon,
    icon,
    id,
    isPageNavCollapsed = false,
    itemKey,
    params,
    label,
    level = 0,
    displayMode = 'default',
    onClick,
    readOnly,
    tooltip,
    searchActive = false,
    searchTerm,
    expandOnSearch = false,
    suffixIcon,
    currentPageItemsInfo,
    expandCurrentPageItems,
}) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const { user } = useContext(UserContext);

    const { isActive, classNames } = usePageNavMenuItem({
        activeItemsInfo,
        id,
        isPageNavCollapsed,
        level,
        params,
        displayMode,
        itemKey,
        searchActive,
        expandOnSearch,
        currentPageItemsInfo,
        expandCurrentPageItems,
    });

    const handleItemClick = () => {
        onClick?.(id, level, false, params);
    };

    const showPopOutButton = () => {
        const routeHasPopOutEnabled = AllRoutes.pageToolbarComponents(id)?.some((c) => c.component === 'popOut');

        const hasStudySelected = !!state?.selectedStudy;

        return routeHasPopOutEnabled && hasStudySelected;
    };
    const showPopOut = showPopOutButton() && !readOnly;
    const muiListItem = (
        <StyledMuiListItem
            id={itemKey}
            className={classNames.listItem}
            style={{ overflow: 'hidden', width: isPageNavCollapsed ? '40px' : '' }}
            data-testid={itemKey}
            onClick={handleItemClick}
            disabled={readOnly}
        >
            {level === 2 && !isActive && <div className={styles.thirdLevelInactiveAdornmentLine} />}
            {level === 2 && isActive && <div className={styles.thirdLevelAdornmentLine} />}
            {(icon || faIcon) && <PageNavMenuItemIcon faIcon={faIcon} icon={icon} isCollapsed={isPageNavCollapsed} />}
            {searchActive ? (
                <ListItemText className={classNames.listItemText} disableTypography>
                    <Highlighted text={label} highlight={searchTerm} />
                </ListItemText>
            ) : (
                !isPageNavCollapsed && (
                    <div className={styles.listItemTextContainer}>
                        <ListItemText className={classNames.listItemText} disableTypography primary={label} />
                        {(suffixIcon || showPopOut) && (
                            <div
                                className={cn({
                                    [styles.suffixIcons]: displayMode === 'default',
                                    [styles.collapsedMenuSuffixIcons]: displayMode === 'collapsed',
                                })}
                            >
                                {suffixIcon && suffixIcon}
                                {showPopOut && (
                                    <button
                                        aria-label="Pop Out Page"
                                        data-test-id="pop-out-button"
                                        type="button"
                                        className={styles.popOutPageButton}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            const route = AllRoutes.path(id, params);
                                            PopOutPage(user?.organisationKey as string, route);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={['far', 'arrow-up-right-from-square']} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )
            )}
        </StyledMuiListItem>
    );

    const content =
        readOnly || isPageNavCollapsed ? (
            <PageNavTooltip title={tooltip ? t(tooltip) : ''}>{muiListItem}</PageNavTooltip>
        ) : (
            muiListItem
        );

    if (isPageNavCollapsed) {
        return content;
    }

    return (
        <RelativeMenuItemPositionWrapper top={searchActive ? 0 : 20} level={level}>
            {content}
        </RelativeMenuItemPositionWrapper>
    );
};

const PageNavSubMenu = ({
    activeItemsInfo,
    collapsed,
    items,
    level,
    isPageNavCollapsed,
    displayMode,
    onClick,
    searchActive,
    searchTerm,
    expandOnSearch,
    currentPageItemsInfo,
    expandCurrentPageItems,
}: PageNavSubMenuProps) => {
    const { t } = useTranslation();

    return (
        <Collapse in={searchActive ? expandOnSearch : !collapsed} timeout="auto" unmountOnExit>
            <List
                component="div"
                disablePadding
                className={cn([styles.subMenu], {
                    [styles.nestedList]: !searchActive,
                    [styles.subMenuDefaultHeight]: displayMode === 'default' && level === 1 && !searchActive,
                    [styles.subMenuBorderBottom]: displayMode === 'default' && level === 1 && !searchActive,
                    [styles.collapsedMenuOverlaySubMenu]: displayMode === 'collapsed' && level === 1 && !searchActive,
                    [styles.secondTierSubMenu]: level === 1 && !searchActive,
                })}
                style={isPageNavCollapsed ? { maxHeight: '100%' } : undefined}
            >
                <TransitionGroup
                    className={cn('k-d-flex k-flex-col k-gap-1', {
                        [styles.thirdLevelContainer]: level === 2,
                    })}
                >
                    {items.map((navItem) => {
                        const navItemProps = {
                            ...navItem,
                            displayMode,
                            activeItemsInfo,
                            onClick,
                            level,
                            id: navItem.key,
                            label:
                                navItem.element === Constants.PageElements.DataViewsCM2 && level === 2
                                    ? navItem.label
                                    : t(navItem.label),
                            itemKey: `nav-${navItem.key}`,
                            isPageNavCollapsed,
                            readOnly: navItem.readOnly,
                            tooltip: navItem.tooltip,
                            searchActive,
                            searchTerm,
                            expandOnSearch: navItem.expandOnSearch,
                            currentPageItemsInfo,
                            expandCurrentPageItems,
                        };
                        const hasSubMenu = Array.isArray(navItem.items) && navItem.items.length > 0;

                        if (hasSubMenu) {
                            return (
                                <Collapse key={navItem.key} timeout="auto">
                                    {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                                    <PageNavMenuItemWithSubMenu
                                        {...navItemProps}
                                        key={navItem.key}
                                        params={navItem.params}
                                    />
                                </Collapse>
                            );
                        }
                        return (
                            // Data views use the same key for different items so we need to add the label to the key
                            // in the transition group so that it can differentiate between them.
                            <Collapse
                                key={`${navItem.key}-${navItem.label?.replace(/\s+/g, '-').toLowerCase()}`}
                                timeout="auto"
                            >
                                <PageNavMenuItem {...navItemProps} key={navItem.key} params={navItem.params} />
                            </Collapse>
                        );
                    })}
                </TransitionGroup>
            </List>

            {isPageNavCollapsed && level === 1 && <div className={styles.subMenuBottomLine} />}
        </Collapse>
    );
};

export const PageNavMenuItemWithSubMenu: React.FC<
    PageNavMenuItemProps & { items?: PageNavItem[]; displayMode?: 'default' | 'collapsed' }
> = ({
    activeItemsInfo,
    faIcon,
    icon,
    id,
    isPageNavCollapsed = false,
    displayMode = 'default',
    itemKey,
    items,
    label,
    level = 0,
    onClick,
    readOnly,
    tooltip,
    searchActive = false,
    searchTerm,
    expandOnSearch = false,
    currentPageItemsInfo,
    expandCurrentPageItems,
}) => {
    const { t } = useTranslation();
    const { isCollapsed, classNames } = usePageNavMenuItem({
        activeItemsInfo,
        id,
        isPageNavCollapsed,
        level,
        displayMode,
        itemKey,
        searchActive,
        expandOnSearch,
        currentPageItemsInfo,
        expandCurrentPageItems,
    });

    const isCollapsedDisplayMode = displayMode === 'collapsed';

    const handleItemWithSubMenuClick = () => {
        onClick?.(id, level, !!items?.length);
        isCollapsed.setFalse();
    };

    const muiListItem = (
        <StyledMuiListItem
            id={itemKey}
            className={cn(classNames.listItem)}
            style={{
                overflow: 'hidden',
                width: isPageNavCollapsed ? '40px' : '',
            }}
            data-testid={itemKey}
            onClick={handleItemWithSubMenuClick}
            disabled={readOnly}
        >
            {!isCollapsedDisplayMode && (icon || faIcon) && (
                <PageNavMenuItemIcon faIcon={faIcon} icon={icon} isCollapsed={isPageNavCollapsed} />
            )}
            {searchActive ? (
                <ListItemText className={classNames.listItemText} disableTypography>
                    <Highlighted text={label} highlight={searchTerm} />
                </ListItemText>
            ) : (
                !isPageNavCollapsed && (
                    <div className={styles.listItemTextContainer}>
                        <ListItemText className={classNames.listItemText} disableTypography primary={label} />
                        {level !== 0 && (
                            <div
                                className={cn({
                                    [styles.suffixIcons]: displayMode === 'default',
                                    [styles.collapsedMenuSuffixIcons]: displayMode === 'collapsed',
                                })}
                            >
                                <FontAwesomeIcon
                                    icon={['fass', 'triangle']}
                                    size="xs"
                                    rotation={isCollapsed.value ? 90 : 180}
                                    className="k-color-white"
                                />
                            </div>
                        )}
                    </div>
                )
            )}
        </StyledMuiListItem>
    );

    const subMenu = !readOnly && !isPageNavCollapsed && (
        <PageNavSubMenu
            activeItemsInfo={activeItemsInfo}
            collapsed={isCollapsed.value}
            displayMode={displayMode}
            items={items ?? []}
            level={level + 1}
            onClick={onClick}
            searchActive={searchActive}
            searchTerm={searchTerm}
            expandOnSearch={expandOnSearch}
            currentPageItemsInfo={currentPageItemsInfo}
            expandCurrentPageItems={expandCurrentPageItems}
        />
    );

    const content =
        readOnly || isPageNavCollapsed ? (
            <PageNavTooltip title={tooltip ? t(tooltip) : ''}>{muiListItem}</PageNavTooltip>
        ) : (
            muiListItem
        );

    if (isPageNavCollapsed) {
        return content;
    }

    return (
        <RelativeMenuItemPositionWrapper top={searchActive ? 0 : 20} level={level}>
            {content}
            <RelativeMenuItemPositionWrapper top={searchActive ? 0 : -20} level={level}>
                {subMenu}
            </RelativeMenuItemPositionWrapper>
        </RelativeMenuItemPositionWrapper>
    );
};
